// Sentry initialisation, gated on the `statistics` cookie consent category.
//
// We deliberately do NOT wrap the Sentry API (that was the old src/modules/Sentry.js
// pain). Call sites import `@sentry/vue` directly. This module owns only three
// things: building the init config, running init exactly once, and the consent
// gate that decides WHEN init may run.
//
// Error/performance monitoring is classified under `statistics` (analytics), so we
// gate on exactly that category. Hard requirement satisfied: no Sentry network
// traffic until statistics consent is active — Sentry.init() (hence the transport)
// does not run before then. Errors before that are not captured.
import type { App } from 'vue'
import * as Sentry from '@sentry/vue'

import { ENV, ENV_DEV_OR_STAGING, ENV_LOCAL, VUE_APP_VERSION, env } from '@/helpers/environment'
import { useCookieConsentStore } from '@/store/cookieConsent'
import { useLocationStore } from '@/store/location'
import { useMainStore } from '@/store/main'

import { logAndDropEvent, makeNoopTransport } from './consoleSink'

// Sentry is error/perf monitoring → the `statistics` (analytics) consent category.
const SENTRY_CONSENT_CATEGORY = 'statistics'

// GlitchTip receives traffic only from production builds (staging ships the same
// artifact). Everywhere else — `npm run serve`, PR previews, the dev deploy — the
// SDK still runs the full capture pipeline so tags/contexts stay debuggable, but
// events are console-logged and dropped instead of sent. See consoleSink.ts.
const SEND_TO_GLITCHTIP = ENV === 'production'

let initialised = false

interface ConsentState {
	categories: Record<string, boolean>
}

/**
 * True once statistics consent is active. In opt-in regions (EU/UK) this flips to
 * true only after the user accepts; in opt-out regions it's true by default until
 * declined — both correctly gate the network transport.
 */
function shouldInitSentry(state: ConsentState): boolean {
	return state.categories[SENTRY_CONSENT_CATEGORY] === true
}

function buildInitialScope() {
	// Read current store state at init time — by the time consent is granted these
	// are populated. The App.vue watcher keeps these tags current after init.
	const main = useMainStore()
	// `appAreaConfig` is store state (typed as any), so reading it needs no assertion.
	// The App.vue watcher keeps funnelId/funnelType current after init if it changes.
	const funnel = useLocationStore().appAreaConfig?.funnel

	return {
		tags: {
			funnelId: funnel?.id,
			funnelType: funnel?.slug,
			countryCode: main.getCountryCodeUppercase,
			currencyCode: main.getCurrencyUppercase,
			language: window.language,
			hostname: window.location.hostname,
		},
		// Opaque per-session id (matches Sentry.setUser in the main store) — never
		// email or other PII.
		user: { id: main.sessionId },
	}
}

// Object keys whose values are PII/PHI and must never reach Sentry. Matched
// case-insensitively against keys when redacting console breadcrumbs.
const PII_KEY_RE =
	/(^|_)(e?mail|name|firstname|lastname|fullname|phone|tel|dob|dateofbirth|birth|address|street|city|zip|postal|postalcode|state|password|passwd|secret|token|authorization|auth|cookie|session|signature|ssn|patient)(_|$)/i

// Deep-clone `value`, replacing PII-keyed fields with '[redacted]' and collapsing
// Error objects to { name, message } so an AxiosError's config/response/headers
// never ride along. Depth- and cycle-bounded.
function redactPii(value: unknown, seen: WeakSet<object> = new WeakSet(), depth = 0): unknown {
	if (value === null || typeof value !== 'object' || depth > 6) return value
	if (seen.has(value)) return '[circular]'
	seen.add(value)
	if (value instanceof Error) return { name: value.name, message: value.message }
	if (Array.isArray(value)) return value.map((item) => redactPii(item, seen, depth + 1))
	const out: Record<string, unknown> = {}
	for (const [key, val] of Object.entries(value)) {
		out[key] = PII_KEY_RE.test(key) ? '[redacted]' : redactPii(val, seen, depth + 1)
	}
	return out
}

// Render one console argument as a readable, redacted string (no "[object Object]").
function readableArg(value: unknown): string {
	if (typeof value === 'string') return value
	try {
		return JSON.stringify(redactPii(value))
	} catch {
		return String(value)
	}
}

// Reformat console breadcrumbs into a readable, PII-redacted form; pass everything
// else through untouched. Never drops a breadcrumb.
function redactConsoleBreadcrumb(breadcrumb: Sentry.Breadcrumb, hint?: Sentry.BreadcrumbHint): Sentry.Breadcrumb {
	if (breadcrumb.category !== 'console') return breadcrumb
	const input = hint?.input
	if (Array.isArray(input)) {
		const parts = input.map(readableArg)
		breadcrumb.message = parts.join(' ')
		if (breadcrumb.data) breadcrumb.data.arguments = parts
	}
	return breadcrumb
}

/** Initialise Sentry exactly once. No-op if already initialised or DSN is unset. */
function initSentry(app: App): void {
	if (initialised) return
	const dsn = env('SENTRY_DSN')
	if (!dsn) return
	initialised = true

	// A user-defined errorHandler must exist BEFORE Sentry.init. Without one the
	// SDK's attachErrorHandler re-throws every Vue render error; Vue catches it
	// again and logs the bare error via console.error, CaptureConsole captures that
	// copy synchronously — and Dedupe then drops the SDK's setTimeout-delayed
	// `vue`-mechanism event, the one carrying componentName/lifecycleHook/trace.
	// Net effect: every render error reached GlitchTip context-less (issues 364/260
	// were undiagnosable). With a handler present the SDK captures WITH vue context
	// and delegates here instead of re-throwing. Logged as warn: console.error
	// would be re-captured by CaptureConsole and recreate the duplicate.
	app.config.errorHandler = (error) => console.warn('[vue error]', error)

	Sentry.init({
		app,
		dsn,
		// Local dev (`npm run serve` sets VITE_APP_VERSION=local) reports as its own
		// `local` environment instead of being lumped in with CI `development`.
		environment: ENV_LOCAL ? 'local' : ENV,
		release: VUE_APP_VERSION,
		// SDK-internal debug logging (integration setup, event lifecycle) — complements
		// the consoleSink event log below.
		debug: ENV_DEV_OR_STAGING,
		// Non-prod: swallow the envelopes that don't go through beforeSend (sessions,
		// client reports) so no request ever reaches GlitchTip. `undefined` keeps the
		// SDK's default fetch transport in prod.
		transport: SEND_TO_GLITCHTIP ? undefined : makeNoopTransport,
		sendDefaultPii: false,
		// @sentry/vue attaches component props to contexts.vue.propsData on every
		// component error (attachProps defaults to true); props can carry PII (a
		// component may receive user/email/etc. as props). Disable that.
		attachProps: false,
		// After our errorHandler runs, the SDK would additionally console.error a
		// "[Vue warn]: Error in <hook>" string (logErrors defaults to true) —
		// CaptureConsole would turn that into a second, message-only event next to
		// the vue-mechanism exception event. One error, one event.
		logErrors: false,
		// extraErrorDataIntegration intentionally removed: it serialises every
		// non-native enumerable prop of an error (and error.cause) into the event.
		// For an AxiosError that means request body, query params, the x-api-token
		// header and response.data — PII/PHI — on EVERY captured network error
		// (interceptor, unhandledrejection, Vue handler, console). Without it a raw
		// error contributes only message + stack, which are safe.
		integrations: [Sentry.captureConsoleIntegration({ levels: ['error'] })],
		// Extension point: this project grows its own ignore list as the dashboard
		// reveals real noise. Intentionally NOT seeded from other projects.
		//
		// In-app browsers inject instrumentation scripts into the pages they open, and
		// those scripts fail on their own: thrown errors land in our global handlers,
		// caught-and-console.error'd ones in CaptureConsole. Nothing in src/ or any
		// dependency references the APIs below, so each signature is provably
		// third-party.
		ignoreErrors: [
			// Meta (FB/IG) iOS — the WKWebView bridge is gone at pagehide/process-swap.
			// Matched by message because iOS gives injected scripts no sourceURL: their
			// frames inherit the document URL, so denyUrls below cannot catch them.
			// GlitchTip issues 301/139/420/340.
			/window\.webkit\.messageHandlers/,
			// Meta Android flavour of the same script — JavascriptInterface already
			// released ("Java object is gone" / "Java exception was raised…"). Only the
			// Android WebView bridge wraps errors in this wording. Issues 361/195.
			/Error invoking postMessage: Java/,
			// TikTok's click tracker (buildClickData) runs `className.slice(0,500)` on
			// every ancestor of a click, but SVG className is an SVGAnimatedString — no
			// .slice. TikTok catches and console.errors the TypeError ("Error handling
			// click event: …"), which CaptureConsole then captures. We never call
			// className.slice. Issues 337/311/335.
			/className\.slice is not a function/,
		],
		denyUrls: [
			// Meta in-app browser injected modules that do carry a sourceURL (Android).
			/^iabjs:\/\//,
			// Scripts injected via WKUserScript (Safari extensions/features) — WebKit
			// masks their origin, so they can never be our shipped code.
			/^webkit-masked-url:\/\//,
		],
		// Prod: extension point for project-specific filtering, identity for now.
		// Non-prod: log the fully assembled event (tags/contexts/extra) and drop it.
		beforeSend: SEND_TO_GLITCHTIP ? (event) => event : logAndDropEvent,
		// Console breadcrumbs otherwise render as "[object Object]" (message) and
		// depth-truncated "[Object]" (data.arguments). Reformat into a readable,
		// PII-redacted string instead of widening normalisation globally.
		beforeBreadcrumb: redactConsoleBreadcrumb,
		initialScope: buildInitialScope(),
	})
}

/**
 * Initialise Sentry. Outside production (PR/dev/local) there is often no consent banner — e.g.
 * Cookiebot is skipped on *.workers.dev preview hosts — so `statistics` consent never flips and
 * Sentry would never start, hiding every error from those environments; there we init
 * immediately. In production we keep the strict gate: init only once statistics consent is active
 * (subscribed before the banner mounts to catch the first accept; also handles the already-
 * consented returning-user / opt-out case). PHI/PII scrubbing in initSentry applies regardless.
 * Consent-wise the early non-prod init is also moot now: outside production nothing is sent at
 * all — events are console-logged and dropped (SEND_TO_GLITCHTIP / consoleSink.ts).
 */
export function setupSentryConsentGate(app: App): void {
	// Consent is enforced only in production; PR/dev/local initialise without waiting for it.
	if (ENV !== 'production') {
		initSentry(app)
		return
	}

	const consent = useCookieConsentStore()

	const tryInit = () => {
		if (shouldInitSentry(consent)) initSentry(app)
	}

	tryInit()
	if (initialised) return

	const unsubscribe = consent.$subscribe(() => {
		tryInit()
		if (initialised) unsubscribe()
	})
}
